/**
 * Random Street View location finder.
 *
 * Picks a random valid Street View location by reading the pixel colors of
 * Google's Street View coverage tiles. Specific blue/cyan pixel ranges in
 * the coverage tiles correspond to actual SV coverage; the algorithm walks
 * the tile pyramid (zooming in at each level toward a tile that has any SV
 * coverage), then samples a random "covered" pixel and converts back to
 * a lat/lng.
 *
 * Why pixel-sampling vs. `StreetViewService.getPanorama()` with random
 * coordinates: most of the world isn't covered, so naive random points
 * have a very low hit rate. This walks the coverage shape directly.
 *
 * Lifted nearly-verbatim from morrell_dev_2's `random_coordinates/`
 * (StreetView.js + Google.js + index.js). Behavior preserved; only the
 * google singleton, EventEmitter base class, and progress events were
 * dropped — call `init(google.maps)` once after the JS API loads.
 */

type MapsNs = typeof google.maps;
type LatLng = google.maps.LatLng;
type Polygon = google.maps.Polygon;

type Type = 'sv' | 'photo' | 'both';
type Distribution = 'weighted' | 'uniform';

type Tile = { x: number; y: number; zoom: number };
type CoverageRow = [number, number]; // [svCount, photoCount]

interface CoveredTile extends Tile {
  coverage: { sv: number; photo: number; both: number };
  types: { sv: boolean; photo: boolean };
  img: HTMLImageElement | false;
}

interface Parameters {
  /** Optional region constraint as `[path][point][lat, lng]`. */
  polygon?: Array<Array<[number, number]>> | null;
  enableCaching?: boolean;
  endZoom?: number;
  cacheKey?: string;
  type?: Type;
  distribution?: Distribution;
}

const COVERAGE_STORAGE_KEY = 'geoguesser-tile-coverage';

const TYPE_COLORS: Array<{ id: 'sv' | 'photo'; color: number[] }> = [
  { color: [84, 160, 185], id: 'sv' },
  { color: [18, 158, 175], id: 'sv' },
  { color: [165, 224, 250, 102], id: 'photo' },
];

class RandomStreetView {
  private maps: MapsNs | null = null;
  private slowCpu = false;
  private polygon: Polygon | false = false;
  private enableCaching = true;
  private cacheKey = 'rsv__world';
  private smallestContainingTile: Tile = { x: 0, y: 0, zoom: 0 };
  private area = 1;
  private endZoom = 14;
  private type: Type = 'sv';
  private distribution: Distribution = 'weighted';

  private canvas = document.createElement('canvas');
  private context: CanvasRenderingContext2D;
  private coverageCache: Record<
    string,
    Record<number, Record<number, Record<number, CoverageRow>>>
  >;

  constructor() {
    this.canvas.width = 256;
    this.canvas.height = 256;
    const ctx = this.canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas 2D context unavailable');
    this.context = ctx;
    this.coverageCache = importCoverageCache();
  }

  init(maps: MapsNs): void {
    this.maps = maps;
  }

  setSlowCpu(slow: boolean): void {
    this.slowCpu = slow;
  }

  setParameters({
    polygon = null,
    enableCaching = true,
    endZoom = 14,
    cacheKey,
    type = 'sv',
    distribution = 'weighted',
  }: Parameters = {}): void {
    if (!this.maps) throw new Error('RandomStreetView.init(google.maps) not called');
    if (!['sv', 'photo', 'both'].includes(type)) {
      console.error("type must be 'sv', 'photo', or 'both'");
    }
    if (endZoom < 11) console.warn('endZoom should not be less than 11');
    if (endZoom > 22) console.error("endZoom can't be higher than 22");

    let mapsPolygon: Polygon | false = false;
    if (Array.isArray(polygon)) {
      const paths = polygon.map((path) =>
        path.map(([lat, lng]) => new this.maps!.LatLng(lat, lng)),
      );
      mapsPolygon = new this.maps.Polygon({
        paths,
        strokeColor: '#00ff7a',
        strokeOpacity: 0.8,
        strokeWeight: 2,
        fillColor: '#00ff7a',
        fillOpacity: 0.35,
        draggable: false,
        clickable: false,
      });
    }

    let resolvedCacheKey = cacheKey ?? '';
    if (!cacheKey && enableCaching && mapsPolygon) {
      mapsPolygon
        .getPaths()
        .forEach((p) => p.forEach((c) => (resolvedCacheKey += c.lat().toString() + c.lng())));
    } else if (!cacheKey && !mapsPolygon) {
      resolvedCacheKey = 'rsv__world';
    }

    this.polygon = mapsPolygon;
    this.cacheKey = resolvedCacheKey || 'rsv__world';
    this.enableCaching = enableCaching;
    this.smallestContainingTile = this.polygonToSmallestContainingTile(mapsPolygon);

    let area = 0;
    if (mapsPolygon) {
      mapsPolygon.getPaths().forEach((path) => {
        area += this.maps!.geometry.spherical.computeArea(path);
      });
    }
    this.area = area;

    this.endZoom = endZoom;
    this.type = type;
    this.distribution = distribution;
  }

  async getRandomLocation(): Promise<{ lat: number; lng: number } | null> {
    if (!this.maps) throw new Error('RandomStreetView.init(google.maps) not called');
    const location = await this.randomValidLocation({
      endZoom: this.endZoom,
      distribution: this.distribution,
      type: this.type,
    });
    if (!location) return null;
    return { lat: location.lat(), lng: location.lng() };
  }

  private async randomValidLocation({
    endZoom,
    type,
    distribution,
  }: {
    endZoom: number;
    type: Type;
    distribution: Distribution;
  }): Promise<LatLng | false> {
    this.distribution = distribution;
    const tile = await this.randomValidTile(endZoom, type, this.smallestContainingTile);
    if (tile === false) return false;

    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context) return false;

    if (tile.img === false) tile.img = await this.getTileImage(tile.x, tile.y, tile.zoom);
    const img = tile.img;
    canvas.width = img.width;
    canvas.height = img.height;
    context.drawImage(img, 0, 0);

    const data = context.getImageData(0, 0, img.width, img.height).data;

    const indices: number[] = [];
    for (let i = 0; i < data.length; i += 4) {
      const color = Array.from(data.slice(i, i + 4));
      const colorType = getColorType(color);
      if (colorType === type || (colorType !== 'empty' && type === 'both')) {
        indices.push(i);
      }
    }

    if (indices.length === 0) {
      console.error('No SV pixel found in selected tile');
      return this.randomValidLocation({ endZoom, type, distribution });
    }

    const randomSvIndex = indices[Math.floor(Math.random() * indices.length)];
    const x = (randomSvIndex / 4) % img.width;
    const y = Math.floor(randomSvIndex / 4 / img.width);
    this.saveCoverageCache();
    return this.tilePixelToCoordinate(tile.x, tile.y, tile.zoom, x, y);
  }

  private async randomValidTile(
    endZoom: number,
    type: Type,
    chosenTile: Tile = { x: 0, y: 0, zoom: 0 },
    startZoom: number = chosenTile.zoom,
  ): Promise<CoveredTile | false> {
    if (chosenTile.zoom >= endZoom) {
      const cached = this.getCoverageCache(chosenTile.x, chosenTile.y, chosenTile.zoom);
      const img = await this.getTileImage(chosenTile.x, chosenTile.y, chosenTile.zoom);
      return { ...chosenTile, ...cached, img };
    }
    const photoSphereZoomLevel = 12;

    let subTiles = await this.getSubTiles(chosenTile.x, chosenTile.y, chosenTile.zoom);

    let validTiles = subTiles.filter(
      (tile) =>
        (type === 'sv' && tile.types.sv) ||
        (type === 'photo' && tile.types.photo) ||
        (type === 'both' && (tile.types.photo || tile.types.sv)) ||
        (tile.zoom <= photoSphereZoomLevel && tile.types.sv),
    );
    validTiles = validTiles.filter((tile) => this.tileIntersectsMap(tile.x, tile.y, tile.zoom));

    if (chosenTile.zoom === startZoom && validTiles.length === 0 && chosenTile.zoom <= 7) {
      // Top-of-tree miss: ignore SV coverage and try anyway.
      validTiles = subTiles.filter((tile) => this.tileIntersectsMap(tile.x, tile.y, tile.zoom));
      startZoom = validTiles[0]?.zoom ?? startZoom;
    }

    const shuffleFun =
      this.distribution === 'uniform'
        ? (array: CoveredTile[]) => shuffle(array)
        : (array: CoveredTile[]) =>
            shuffleWeighted(array, (item) =>
              chosenTile.zoom + 1 <= photoSphereZoomLevel
                ? item.coverage.both
                : (item.coverage as unknown as Record<Type, number>)[type] ?? 0,
            );
    const shuffledTiles = shuffleFun(validTiles);

    for (const tile of shuffledTiles) {
      const subTile = await this.randomValidTile(endZoom, type, tile, startZoom);
      if (subTile !== false && (subTile.types.sv || subTile.types.photo)) return subTile;
    }
    return false;
  }

  private async getSubTiles(x: number, y: number, zoom: number): Promise<CoveredTile[]> {
    const startX = x * 2;
    const startY = y * 2;
    const tasks: Array<Promise<CoveredTile>> = [];
    for (let yy = startY; yy < startY + 2; yy++) {
      for (let xx = startX; xx < startX + 2; xx++) {
        tasks.push(this.getTile(xx, yy, zoom + 1));
      }
    }
    return Promise.all(tasks);
  }

  private async getTile(x: number, y: number, zoom: number): Promise<CoveredTile> {
    if (this.coverageCacheContains(x, y, zoom)) {
      const { coverage, types } = this.getCoverageCache(x, y, zoom);
      return { coverage, types, img: false, x, y, zoom };
    }
    const img = await this.getTileImage(x, y, zoom);
    const c = await this.getTileCoverage(x, y, zoom, img);
    this.setCoverageCache(x, y, zoom, c);
    const { coverage, types } = this.getCoverageCache(x, y, zoom);
    return { coverage, types, img, x, y, zoom };
  }

  private async getTileImage(x: number, y: number, zoom: number): Promise<HTMLImageElement> {
    return new Promise((resolve) => {
      const url = `https://maps.googleapis.com/maps/vt?pb=!1m5!1m4!1i${zoom}!2i${x}!3i${y}!4i256!2m8!1e2!2ssvv!4m2!1scb_client!2sapiv3!4m2!1scc!2s*211m3*211e3*212b1*213e2*211m3*211e2*212b1*213e2!3m3!3sUS!12m1!1e68!4e0`;
      void fetch(url).then(async (response) => {
        const blob = await response.blob();
        const img = new Image();
        img.src = URL.createObjectURL(blob);
        img.onload = () => resolve(img);
      });
    });
  }

  private async getTileCoverage(
    tileX: number,
    tileY: number,
    zoom: number,
    img: HTMLImageElement,
  ): Promise<CoverageRow> {
    this.context.clearRect(0, 0, this.canvas.width, this.canvas.height);
    this.context.drawImage(img, 0, 0);
    const data = this.context.getImageData(0, 0, img.width, img.height).data;

    const coverage: CoverageRow = [0, 0];
    const isFullyContained =
      this.polygon !== false && this.isTileFullyContainedInMap(tileX, tileY, zoom);

    const massiveArea = 5_000_000_000_000;
    const bigArea = 1_000_000_000_000;
    let chunkSize = 16;
    if (zoom <= 2 && this.area < massiveArea) chunkSize = 4;
    else if (zoom <= 4 && this.area < massiveArea) chunkSize = 4;
    else if (zoom <= 7 && this.area < bigArea) chunkSize = 8;

    let pixelChunkSize: number;
    if (zoom <= 6) pixelChunkSize = 16;
    else if (zoom <= 7) pixelChunkSize = 16;
    else if (zoom <= 8) pixelChunkSize = 8;
    else if (zoom <= 9) pixelChunkSize = 4;
    else pixelChunkSize = 2;
    pixelChunkSize = Math.min(pixelChunkSize, chunkSize);

    for (let y = 0; y < img.height; y += chunkSize) {
      for (let x = 0; x < img.width; x += chunkSize) {
        if (this.slowCpu) await waitSleep(10);
        if (!isFullyContained) {
          const coordinate = this.tilePixelToCoordinate(
            tileX,
            tileY,
            zoom,
            x + chunkSize / 2,
            y + chunkSize / 2,
          );
          if (!this.containsLocation(coordinate, this.polygon)) continue;
        }
        for (let pY = y + pixelChunkSize / 2; pY < y + chunkSize; pY += pixelChunkSize) {
          for (let pX = x + pixelChunkSize / 2; pX < x + chunkSize; pX += pixelChunkSize) {
            const i = (pY * img.width + pX) * 4;
            const color = Array.from(data.slice(i, i + 4));
            const colorType = getColorType(color);
            if (colorType === 'sv') coverage[0]++;
            if (colorType === 'photo') coverage[1]++;
          }
        }
      }
    }
    return coverage;
  }

  private tilePixelToCoordinate(
    tileX: number,
    tileY: number,
    zoom: number,
    pixelX: number,
    pixelY: number,
  ): LatLng {
    const tx = (tileX + pixelX / 256) * 2 ** (8 - zoom);
    const ty = (tileY + pixelY / 256) * 2 ** (8 - zoom);
    const lng = (tx / 256) * 360 - 180;
    const n = Math.PI - (2 * Math.PI * ty) / 256;
    const lat = (180 / Math.PI) * Math.atan(0.5 * (Math.exp(n) - Math.exp(-n)));
    return new this.maps!.LatLng(lat, lng);
  }

  private getTileCornerCoordinates(tileX: number, tileY: number, zoom: number): LatLng[] {
    return [
      this.tilePixelToCoordinate(tileX, tileY, zoom, 0, 0),
      this.tilePixelToCoordinate(tileX, tileY, zoom, 256, 0),
      this.tilePixelToCoordinate(tileX, tileY, zoom, 256, 256),
      this.tilePixelToCoordinate(tileX, tileY, zoom, 0, 256),
    ];
  }

  private tileIntersectsMap(tileX: number, tileY: number, zoom: number): boolean {
    if (this.polygon === false) return true;
    const corners = this.getTileCornerCoordinates(tileX, tileY, zoom);
    for (const c of corners) {
      if (this.containsLocation(c, this.polygon)) return true;
    }
    // Tile corners not in polygon doesn't necessarily mean disjoint — the
    // polygon may pass through the tile interior. Fall back to a bounds
    // intersection check.
    const mapsBounds = new this.maps!.LatLngBounds();
    for (const c of corners) mapsBounds.extend(c);

    let mapContains = false;
    this.polygon.getPaths().forEach((path) => {
      path.forEach((point) => {
        if (mapsBounds.contains(point)) mapContains = true;
      });
    });
    return mapContains;
  }

  private isTileFullyContainedInMap(tileX: number, tileY: number, zoom: number): boolean {
    const corners = this.getTileCornerCoordinates(tileX, tileY, zoom);
    for (const c of corners) {
      if (!this.containsLocation(c, this.polygon)) return false;
    }
    return true;
  }

  private containsLocation(location: LatLng, polygon: Polygon | false): boolean {
    if (polygon === false) return true;
    return this.maps!.geometry.poly.containsLocation(location, polygon);
  }

  private polygonToSmallestContainingTile(polygon: Polygon | false): Tile {
    if (polygon === false) return { x: 0, y: 0, zoom: 0 };
    const bounds = new this.maps!.LatLngBounds();
    polygon.getPaths().forEach((path) => {
      path.forEach((pos) => bounds.extend(pos));
    });
    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();
    let resultTile: Tile = { x: 0, y: 0, zoom: 0 };
    for (let zoom = 0; zoom <= 18; zoom++) {
      const neTile = this.coordinateToTile(ne, zoom);
      const swTile = this.coordinateToTile(sw, zoom);
      if (neTile.x !== swTile.x || neTile.y !== swTile.y) break;
      resultTile = neTile;
    }
    return resultTile;
  }

  private coordinateToTile(coordinate: LatLng, zoom: number): Tile {
    const latRad = (coordinate.lat() * Math.PI) / 180;
    const n = 2 ** zoom;
    const x = Math.floor(((coordinate.lng() + 180) / 360) * n);
    const y = Math.floor(
      ((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2) * n,
    );
    return { x, y, zoom };
  }

  private coverageCacheContains(x: number, y: number, zoom: number): boolean {
    const id = this.cacheKey;
    return Boolean(this.coverageCache[id]?.[zoom]?.[x]?.[y]);
  }

  private getCoverageCache(
    x: number,
    y: number,
    zoom: number,
  ): { types: { sv: boolean; photo: boolean }; coverage: { sv: number; photo: number; both: number } } {
    const id = this.cacheKey;
    const [svCoverage, photoCoverage] = this.coverageCache[id][zoom][x][y];
    return {
      types: { sv: svCoverage > 0, photo: photoCoverage > 0 },
      coverage: { sv: svCoverage, photo: photoCoverage, both: svCoverage + photoCoverage },
    };
  }

  private setCoverageCache(x: number, y: number, zoom: number, value: CoverageRow): void {
    const id = this.cacheKey;
    if (!this.coverageCache[id]) this.coverageCache[id] = {};
    if (!this.coverageCache[id][zoom]) this.coverageCache[id][zoom] = {};
    if (!this.coverageCache[id][zoom][x]) this.coverageCache[id][zoom][x] = {};
    this.coverageCache[id][zoom][x][y] = value;
  }

  private saveCoverageCache(): void {
    if (this.enableCaching) {
      try {
        localStorage.setItem(COVERAGE_STORAGE_KEY, JSON.stringify(this.coverageCache));
      } catch {
        // Quota exceeded — silently drop cache; we can still play.
      }
    }
  }
}

const importCoverageCache = (): RandomStreetView['coverageCache'] => {
  try {
    const raw = localStorage.getItem(COVERAGE_STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

const getColorType = (rgba: number[]): 'sv' | 'photo' | 'empty' => {
  if (rgba[2] === 0) return 'empty';
  const allowedColorDiff = 4;
  typeLoop: for (const { id, color } of TYPE_COLORS) {
    for (let i = 0; i < color.length; i++) {
      if (Math.abs(color[i] - rgba[i]) > allowedColorDiff) continue typeLoop;
    }
    return id;
  }
  return 'empty';
};

const waitSleep = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

const shuffle = <T>(input: T[]): T[] => {
  for (let i = input.length - 1; i >= 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [input[i], input[j]] = [input[j], input[i]];
  }
  return input;
};

const shuffleWeighted = <T>(array: T[], weightField: (item: T) => number): T[] => {
  if (array.length === 0) return array;
  const result: T[] = [];
  const len = array.length;
  let totalWeights = array.map(weightField).reduce((a, b) => a + b, 0);
  for (let i = 0; i < len; i++) {
    let randomWeightValue = Math.random() * totalWeights;
    let weightedRandomIndex = -1;
    for (let j = 0; j < array.length; j++) {
      const item = array[j];
      if (weightField(item) > randomWeightValue) {
        weightedRandomIndex = j;
        break;
      }
      randomWeightValue -= weightField(item);
    }
    if (weightedRandomIndex === -1) weightedRandomIndex = 0;
    const item = array.splice(weightedRandomIndex, 1)[0];
    totalWeights -= weightField(item);
    result.push(item);
  }
  return result;
};

export const randomStreetView = new RandomStreetView();
